import { inject, singleton } from 'tsyringe';
import { LoggerService } from 'app-core/src/services/logger.service';
import {
  kNameOfNotificationWindowService,
  INotificationWindowService,
} from 'app-core/src/interfaces/notification-window-service.interface';
import {
  kNameOfUserActionService,
  IUserActionService,
  IUserActionServiceDelegate,
} from 'app-core/src/interfaces/user-action-service.interface';
import { INotificationDisplayDetails } from 'app-core/src/models/notification-display-details.model';
import { OWWindowsListenerDelegate } from '../odk-ts';
import { TrackEvent } from 'app-core/src/models/tracking-event.model';
import { IOWWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-wrapper.interface';
import { IOWWindowsListenerDelegateProvider } from 'app-core/src/interfaces/ow-wrappers/ow-window-listener-delegate-wrapper.interface';
import { IframeCommunicationServiceImpl } from 'app-core/src/services/iframe-communications-service/iframe-communication.service';
import {
  kDITokenWindowListener,
  kDITOWWrapper,
} from 'app-core/src/constants/injectionTokens';
import { IframeCommunicationDelegate } from 'app-core/src/interfaces/iframe-communication.interface';

const KNotificationWindowName = 'notification';
// -----------------------------------------------------------------------------
@singleton()
export class NotificationController
  implements
    IframeCommunicationDelegate,
    OWWindowsListenerDelegate,
    IUserActionServiceDelegate
{
  private readonly logger = LoggerService.getCategory('NotificationController');
  private activeNotificationInfo: INotificationDisplayDetails;
  private activeNotificationIframe: HTMLIFrameElement;
  private notificationTimeoutDisabled = false;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kNameOfNotificationWindowService)
    private readonly notificationWindowService: INotificationWindowService,
    @inject(kNameOfUserActionService)
    private readonly userActionService: IUserActionService,
    private readonly iframeComsService: IframeCommunicationServiceImpl,
    @inject(kDITokenWindowListener)
    private readonly owWindowListener: IOWWindowsListenerDelegateProvider,
    @inject(kDITOWWrapper)
    private readonly owWrapper: IOWWrapper,
  ) {
    this.registerListeners();
  }

  // ---------------------------------------------------------------------------
  public async run() {
    try {
      this.activeNotificationInfo =
        await this.notificationWindowService.getActiveNotification();

      this.logger.info(
        `Running active notification: ${JSON.stringify(
          this.activeNotificationInfo,
        )}`,
      );

      const { url } = this.activeNotificationInfo;

      if (this.isIframeAddressValid(url)) {
        this.setIframeInMainDiv(url);
      }
    } catch (ex) {
      this.logger.error('Error while loading active Notification:', ex);
      this.userActionService.sendErrorTrackingEvent(
        this.activeNotificationInfo,
      );

      this.toggleNotificationTimeoutTimer(true);
      this.unregisterIframe();
      this.notificationWindowService.closeNotificationWindow();
    }
  }

  // ---------------------------------------------------------------------------
  private async registerListeners() {
    this.iframeComsService.addListener(this);
    this.owWindowListener.addListener(this);
    this.unregisterListeners();
    this.owWindowListener.addListener(this);
    this.userActionService.addListener(this);
  }

  // ---------------------------------------------------------------------------
  private toggleNotificationTimeoutTimer(pause: boolean) {
    if (!this.notificationTimeoutDisabled) {
      this.notificationWindowService.toggleTimeoutTimer(pause);
    }
  }

  // ---------------------------------------------------------------------------
  private async unregisterListeners() {
    this.owWindowListener.removeListener(this);
  }

  // ---------------------------------------------------------------------------
  private unregisterIframe() {
    this.activeNotificationIframe = null;
    this.iframeComsService.deregisterIframe();
  }

  // ---------------------------------------------------------------------------
  private async isIframeAddressValid(url: any) {
    try {
      const response = await fetch(url, {
        method: 'HEAD',
      });
      if (!response.ok) {
        console.error(
          '[Notification Window] Error loading iframeURL.',
          response.status,
          response.statusText,
        );
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  private setIframeInMainDiv(srcUrl: string) {
    try {
      this.activeNotificationIframe = document.createElement('iframe');
      this.activeNotificationIframe.setAttribute(
        'style',
        'overflow:hidden;height:100%;width:100%;display:block;',
      );
      this.activeNotificationIframe.id = 'notif-iframe';
      this.activeNotificationIframe.setAttribute('src', srcUrl);
      document
        .getElementById('main')
        .appendChild(this.activeNotificationIframe);

      this.iframeComsService.registerIframe(this.activeNotificationIframe);
    } catch (e) {
      this.logger.error('Error setting iframe in main div', e);
    }
  }

  // ---------------------------------------------------------------------------
  private isAltF4patchNeeded(): boolean {
    try {
      const altF4PatchVersion = '0.198.0.0';
      const owVersion = this.owWrapper.getVersion();
      const compareResult = altF4PatchVersion.localeCompare(owVersion);
      const performPatch: boolean = compareResult > 0;

      this.logger.info(
        `Alt+F4 patch - should perform patch? - ${performPatch}`,
      );

      return performPatch;
    } catch (e) {
      this.logger.error(
        'Error checking if altF4 patch is required, Error details:',
        e,
      );
      return true;
    }
  }

  // ---------------------------------------------------------------------------
  // IframeCommunication Delegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onDirectInstallApplicationRequested() {
    this.userActionService.installApplication(this.activeNotificationInfo);
    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  onOpenApplicationInStoreRequested() {
    this.userActionService.openApplicationInStore(this.activeNotificationInfo);
    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  onCloseNotificationRequested() {
    this.userActionService.notificationDismissed(
      this.activeNotificationInfo,
      this.notificationWindowService.getNotificationStopWatchTime() / 1000,
    );

    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  onOpenNotificationSettingsRequested() {
    this.userActionService.openNotificationSettings(
      this.activeNotificationInfo,
    );
    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  onTurnOffNotificationsRequested() {
    this.userActionService.turnOffNotifications(this.activeNotificationInfo);
    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  onSendTrackingRequested(event: TrackEvent) {
    this.userActionService.sendTrackingEvent(event);
  }

  // ---------------------------------------------------------------------------
  onToggleTimeoutTimerRequested(event: any) {
    this.toggleNotificationTimeoutTimer(event);
  }

  // ---------------------------------------------------------------------------
  onRecommendationInView() {
    this.notificationWindowService.notificationBecameVisible();
  }

  // ---------------------------------------------------------------------------
  onIframeGameExcluded() {
    this.userActionService.gameExcluded(this.activeNotificationInfo.gameId);

    setTimeout(() => {
      this.notificationWindowService.closeNotificationWindow();
    }, 0);
  }

  // ---------------------------------------------------------------------------
  onIframeApplicationExcluded() {
    this.userActionService.applicationExcluded(
      this.activeNotificationInfo.applicationUid,
    );

    setTimeout(() => {
      this.notificationWindowService.closeNotificationWindow();
    }, 0);
  }

  // ---------------------------------------------------------------------------
  onOpenExternalUrl(url: string) {
    this.userActionService.OpenExternalUrl(url, this.activeNotificationInfo);
    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  OnNotificationABTest(params: any) {
    this.userActionService.notificationABTest(params);
  }

  // ---------------------------------------------------------------------------
  OnDownloadExternalApplication(downloadAppId: string) {
    this.userActionService.OnDownloadExternalApplication(
      downloadAppId,
      this.activeNotificationInfo,
    );

    this.toggleNotificationTimeoutTimer(true);
    this.notificationTimeoutDisabled = true;
  }

  // ---------------------------------------------------------------------------
  OnCancelDownloadExternal() {
    this.userActionService.OnCancelDownloadExternalApplication(
      this.activeNotificationInfo,
    );
  }

  // ---------------------------------------------------------------------------
  OnCloseOnUIError() {
    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  OnCloseWhileDownloadExternal() {
    this.userActionService.OnCancelDownloadExternalApplication(
      this.activeNotificationInfo,
    );
    this.userActionService.notificationDismissedWhileDownloading(
      this.activeNotificationInfo,
      this.notificationWindowService.getNotificationStopWatchTime() / 1000,
    );

    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  onCloseNotificationAfterDownload() {
    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  // OWWindowsListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // This function is used as a fallback scenario for Overwolf clients
  // below v0.198.0.0, where a window listener is notified of an AltF4Blocked
  // event only if the window listener is in the same window on which alt+F4 is
  // performed.
  // The non fallback onAltF4Blocked function is implemented in the
  // NotificationWindowService of the background window.
  // ---------------------------------------------------------------------------
  onAltF4Blocked(windowName: string) {
    if (
      !windowName.endsWith(KNotificationWindowName) ||
      !this.isAltF4patchNeeded()
    ) {
      return;
    }

    this.logger.info(
      `received Alt+F4 key combination on windowId: ${windowName},` +
        ' closing window',
    );
    const watchTime =
      this.notificationWindowService.getNotificationStopWatchTime() / 1000;

    this.userActionService.notificationDismissed(
      this.activeNotificationInfo,
      watchTime,
    );
    this.unregisterListeners();
    this.notificationWindowService.closeNotificationWindow();
  }

  // ---------------------------------------------------------------------------
  // IUserActionServiceDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onDownloadCompleted() {
    this.iframeComsService.sendIframeMessage({ downloadStatus: 'complete' });
  }

  // ---------------------------------------------------------------------------
  onDownloadStarted() {
    this.iframeComsService.sendIframeMessage({ downloadStatus: 'started' });
  }

  // ---------------------------------------------------------------------------
  onDownloadProgress(progress: string) {
    this.iframeComsService.sendIframeMessage({
      downloadStatus: 'progress',
      progress: progress,
    });
  }

  // ---------------------------------------------------------------------------
  onDownloadError() {
    this.iframeComsService.sendIframeMessage({ downloadStatus: 'error' });
    this.notificationTimeoutDisabled = false;
    this.toggleNotificationTimeoutTimer(false);
  }

  // ---------------------------------------------------------------------------
  onFileExecuted(result: boolean) {
    const status = result ? 'executed' : 'executionFailed';
    this.logger.info(`File execution result: ${result}`);
    this.iframeComsService.sendIframeMessage({ downloadStatus: status });
    this.notificationTimeoutDisabled = false;
    this.toggleNotificationTimeoutTimer(false);
  }
}
